import React from 'react';

import { View, Text, StyleSheet, Image } from '@react-pdf/renderer';

const BORDER_COLOR = '#8A9EAF';
const BORDER_STYLE = 'solid';

const styles = StyleSheet.create({
  torque: {
    fontSize: 9,
    fontFamily: 'Roboto',
    fontWeight: '600',
    lineHeight: '1.5px',
    color: '#6921C4',
  },
  headerContainer: {
    marginBottom: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    left: 0,
    right: 0,
    paddingTop: 10,
    paddingBottom: 10,
  },
  flex__start: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexGrow: 5,
    width: 50 + '%',
  },
  flex__end: {
    width: 50 + '%',
    alignItems: 'flex-end',
  },
  leftColumn: {
    flexDirection: 'column',
    flexGrow: 9,
  },
  tableRow__heading: {
    margin: 'auto',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 100 + '%',
  },
  estimated__total__heading: {
    fontSize: 9,
    fontFamily: 'Roboto',
    fontWeight: '600',
    marginTop: 15,
    marginBottom: 5,
  },
  note__sub__heading: {
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: '600',
    marginBottom: 5,
  },
  estimated__total__para: {
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: '400',
    paddingTop: 7,
  },
  estimated__total__sig: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
    marginTop: 5,
    border: '1px solid #8A9EAF',
    width: '150px',
    height: '100px',
  },
  table: {
    display: 'table',
    width: 90 + '%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderRadius: 0,
    borderBottomWidth: 0,
    overflow: 'hidden',
    marginTop: 10,
  },
  tableCellHeader: {
    margin: 5,
    fontSize: 8,
    fontFamily: 'Roboto',
    fontWeight: '600',
  },
  tableCell: {
    margin: 5,
    fontSize: 7,
    fontFamily: 'Roboto',
    fontWeight: '400',
  },
  tableRow: {
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderBottomWidth: 1,
    borderRightWidth: 0,
    borderTopRightRadius: 0,
  },
  table__total: {
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 1,
    borderRightWidth: 0,
    backgroundColor: 'rgba(231, 236, 244, .8)',
  },
  details__header: {
    width: 100 + '%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  signatureImage: {
    width: '140px',
    height: '100px',
    objectFit: 'fill',
  },
});

export default function InvoiceTotal({ data, userId }) {
  let extraAmount = 0;
  const {
    id,
    subTotals = '',
    invoice = '',
    currencySymbol = '$',
    termsAndConditions = '',
    invoiceTemplate = [],
    addCommasToNumber,
    notes = ' ',
    totalAppliedTaxes,
  } = data || '';

  const notesAfterPayment = data?.invoice?.notesAfterPayment || '';

  const paymentNotes = data?.invoice?.payments?.[0]?.notes || '';

  const sigDirName = `roSignatures/${process.env.REACT_APP_ENV}/${userId}/${id}`;
  const signatureLink = `${process.env.REACT_APP_AWS_S3_URL}/${sigDirName}/roSignature.jpeg`;

  extraAmount = data?.extraAmount; // in negate case
  const { supplyFeeType, creditCardFeeType } = invoice;
  let {
    subTotal = 0,
    totalDiscount = 0,
    grandTotal = 0,
    remainingAmount = 0,
    paidAmount = 0,
    laborTip = 0,
    supplyFee,
    calculatedSupplyFee,
    calculatedCreditCardFee,
    creditCardFee,
    // extraAmount = 0,
  } = invoice;
  if (invoice?.extraAmount !== 0) {
    extraAmount = invoice?.extraAmount;
  }

  // grandTotal -> contains invoice grand total + labor tip +
  // credit card fee + supply fee + extra amount
  grandTotal =
    (+grandTotal || 0) +
    (+laborTip || 0) +
    (+calculatedCreditCardFee || 0) +
    (+calculatedSupplyFee || 0) +
    (+extraAmount || 0);

  let {
    totalLabor = 0,
    totalMiscellaneous = 0,
    totalPartsSupplies = 0,
    totalTax = 0,
  } = subTotals;

  totalLabor = addCommasToNumber(Math.round((+totalLabor || 0) * 100) / 100);
  totalPartsSupplies = addCommasToNumber(
    Math.round((+totalPartsSupplies || 0) * 100) / 100,
  );
  totalMiscellaneous = addCommasToNumber(
    Math.round((+totalMiscellaneous || 0) * 100) / 100,
  );
  subTotal = addCommasToNumber(Math.round((+subTotal || 0) * 100) / 100);
  totalDiscount = addCommasToNumber(
    Math.round((+totalDiscount || 0) * 100) / 100,
  );
  totalTax = addCommasToNumber(Math.round((+totalTax || 0) * 100) / 100);
  grandTotal = addCommasToNumber(Math.round((+grandTotal || 0) * 100) / 100);
  laborTip = addCommasToNumber(Math.round((+laborTip || 0) * 100) / 100);
  extraAmount = addCommasToNumber(Math.round((+extraAmount || 0) * 100) / 100);
  supplyFee = addCommasToNumber(Math.round((+supplyFee || 0) * 100) / 100);
  calculatedSupplyFee = addCommasToNumber(
    Math.round((+calculatedSupplyFee || 0) * 100) / 100,
  );
  creditCardFee = addCommasToNumber(
    Math.round((+creditCardFee || 0) * 100) / 100,
  );
  calculatedCreditCardFee = addCommasToNumber(
    Math.round((+calculatedCreditCardFee || 0) * 100) / 100,
  );

  remainingAmount = addCommasToNumber(
    Math.round((+remainingAmount || 0) * 100) / 100,
  );
  paidAmount = addCommasToNumber(Math.round((+paidAmount || 0) * 100) / 100);

  // making invoice according to invoice template
  const grandTotalsInfo =
    invoiceTemplate.find((temp) => temp.title?.toLowerCase() === 'grand totals')
      ?.options || '';

  const termsAndCondCheck =
    invoiceTemplate.find((temp) => temp['termsAndConditions'] === true) ||
    false;

  return (
    <>
      <View style={styles.headerContainer}>
        <View style={styles.flex__start}>
          {termsAndCondCheck && (
            <View style={styles.tableRow__heading} wrap>
              <Text style={styles.estimated__total__heading}>
                Terms & Conditions:
              </Text>
              <Text style={styles.estimated__total__para}>
                {termsAndConditions}
              </Text>
            </View>
          )}
          {(notes !== '' ||
            paymentNotes !== '' ||
            notesAfterPayment !== '') && (
            <View style={styles.tableRow__heading}>
              <View>
                <Text style={styles.estimated__total__heading}>Notes</Text>
              </View>
              {notes !== '' && (
                <View style={styles.tableRow__heading}>
                  <Text style={styles.note__sub__heading}>
                    Appointment:
                    <Text style={styles.estimated__total__para}> {notes}</Text>
                  </Text>
                </View>
              )}
              {paymentNotes !== '' && (
                <View style={styles.tableRow__heading}>
                  <Text style={styles.note__sub__heading}>
                    Invoice:
                    <Text style={styles.estimated__total__para}>
                      {paymentNotes}
                    </Text>
                  </Text>
                </View>
              )}
              {notesAfterPayment !== '' && (
                <View style={styles.tableRow__heading}>
                  <Text style={styles.note__sub__heading}>
                    Notes After Payment:
                    <Text style={styles.estimated__total__para}>
                      {notesAfterPayment}
                    </Text>
                  </Text>
                </View>
              )}
            </View>
          )}
          <View style={styles.tableRow__heading} wrap={false}>
            <Text style={styles.estimated__total__heading}>Signature</Text>
            <View style={styles.estimated__total__sig}>
              <Image src={signatureLink} style={styles.signatureImage} />
            </View>
          </View>
        </View>
        <View style={styles.flex__end} wrap={false}>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              {grandTotalsInfo?.['Total Labor'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Labor</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalLabor}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Parts & Supplies'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Parts & Supplies</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalPartsSupplies}
                  </Text>
                </View>
              )}

              {grandTotalsInfo?.['Total Miscellaneous'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Miscellaneous</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalMiscellaneous}
                  </Text>
                </View>
              )}

              {grandTotalsInfo?.['Sub Total'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Subtotal</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {subTotal}
                  </Text>
                </View>
              )}

              {grandTotalsInfo?.['Total Discount'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Discount</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalDiscount}
                  </Text>
                </View>
              )}

              {grandTotalsInfo?.['Total Tax'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Total Tax</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {totalTax}
                  </Text>
                </View>
              )}
              {grandTotalsInfo?.['Total Tax'] &&
                totalAppliedTaxes?.map((tax) => (
                  <View style={styles.details__header}>
                    <Text style={[styles.tableCell, { fontWeight: 700 }]}>
                      {tax?.name}: {currencySymbol}
                      {addCommasToNumber(
                        Math.round((+tax?.calculatedTax || 0) * 100) / 100,
                      )}
                    </Text>
                  </View>
                ))}
              {grandTotalsInfo?.['Labor Tip'] && (
                <View style={styles.details__header}>
                  <Text style={styles.tableCell}>Labor Tip</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {laborTip}
                  </Text>
                </View>
              )}
              <View style={styles.details__header}>
                <Text style={styles.tableCell}>Extra Amount</Text>
                <Text style={styles.tableCellHeader}>
                  {currencySymbol}
                  {extraAmount}
                </Text>
              </View>

              <View style={styles.details__header}>
                <View style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text style={[styles.tableCell, { marginBottom: 0 }]}>
                    Credit Card Fee
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        marginTop: 0,
                        fontSize: 5,
                        fontFamily: 'Roboto',
                        fontWeight: '300',
                      },
                    ]}
                  >
                    (calculated Credit Card Fee:{' '}
                    <Text
                      style={{
                        fontWeight: 800,
                      }}
                    >
                      {currencySymbol}
                      {calculatedCreditCardFee}
                    </Text>
                    )
                  </Text>
                </View>

                <Text style={styles.tableCellHeader}>
                  {creditCardFeeType === 'fixed' ? currencySymbol : ''}
                  {creditCardFee}
                  {creditCardFeeType === 'percentage' ? '%' : ''}
                </Text>
              </View>
              <View style={styles.details__header}>
                <View style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text style={[styles.tableCell, { marginBottom: 0 }]}>
                    Supply Fee
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        marginTop: 0,
                        fontSize: 5,
                        fontFamily: 'Roboto',
                        fontWeight: '300',
                      },
                    ]}
                  >
                    (calculated Supply Fee:{' '}
                    <Text
                      style={{
                        fontWeight: 800,
                      }}
                    >
                      {currencySymbol}
                      {calculatedSupplyFee}
                    </Text>
                    )
                  </Text>
                </View>

                <Text style={styles.tableCellHeader}>
                  {supplyFeeType === 'fixed' ? currencySymbol : ''}
                  {supplyFee}
                  {supplyFeeType === 'percentage' ? '%' : ''}
                </Text>
              </View>
            </View>
            {grandTotalsInfo?.['Grand Totals'] && (
              <View style={styles.table__total}>
                <View style={styles.details__header}>
                  <Text style={styles.tableCellHeader}>Grand Total</Text>
                  <Text style={styles.tableCellHeader}>
                    {currencySymbol}
                    {grandTotal}
                  </Text>
                </View>
              </View>
            )}
            <View style={styles.table__total}>
              <View style={styles.details__header}>
                <Text style={styles.tableCellHeader}>Paid Amount</Text>
                <Text style={styles.tableCellHeader}>
                  {currencySymbol}
                  {paidAmount}
                </Text>
              </View>
            </View>
            <View style={styles.table__total}>
              <View style={styles.details__header}>
                <Text style={styles.tableCellHeader}>Remaining Amount:</Text>
                <Text style={styles.tableCellHeader}>
                  {currencySymbol}
                  {remainingAmount}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    </>
  );
}
